import { t } from "@lingui/macro";
import { CopySimpleIcon } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
} from "@reactive-resume/ui";
import { useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { findResumeById } from "@/client/services/resume";
import { useMyResumes } from "@/client/services/resume/my-resumes";
import { reuseResumeData } from "@/client/services/resume/reuse-data";
import { debouncedUpdateResume, updateResume } from "@/client/services/resume/update";
import { useUser } from "@/client/services/user";
import { useResumeStore } from "@/client/stores/resume";

export const ReuseResumeDialog = () => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const { user } = useUser();
  const currentId = useResumeStore((state) => state.resume.id);
  const locked = useResumeStore((state) => state.resume.locked);
  const { resumes, loading, error } = useMyResumes(user?.id);
  const choices = (resumes ?? []).filter((resume) => resume.id !== currentId);

  const handleImport = async () => {
    if (!selectedId || saving || locked) return;
    setSaving(true);
    setFailure("");
    try {
      const source = await findResumeById({ id: selectedId });
      await debouncedUpdateResume.flush();
      const current = useResumeStore.getState().resume;
      if (current.id !== currentId || current.locked || source.userId !== user?.id) return;
      const saved = await updateResume({
        id: currentId,
        data: reuseResumeData(current.data, source.data),
      });
      if (useResumeStore.getState().resume.id !== currentId) return;
      useResumeStore.setState({ resume: saved });
      setOpen(false);
      toast({ variant: "success", title: t`Les données du CV ont été reprises.` });
    } catch {
      setFailure(
        t`Impossible de reprendre ce CV. Vos données actuelles ont été conservées. Réessayez.`,
      );
    } finally {
      setSaving(false);
    }
  };

  // Le bandeau n’apparaît que si un autre CV peut être repris.
  if (!open && (locked || choices.length === 0)) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) {
          setOpen(next);
          setSelectedId("");
          setFailure("");
        }
      }}
    >
      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm">
            <CopySimpleIcon aria-hidden size={20} weight="duotone" />
          </span>
          <div>
            <p className="text-sm font-bold text-[#071451]">{t`Vous avez déjà un CV ?`}</p>
            <p className="mt-0.5 text-xs leading-5 text-indigo-900/70">
              {t`Reprenez ses informations, sa photo et ses rubriques en un clic.`}
            </p>
          </div>
        </div>
        <DialogTrigger asChild>
          <Button
            type="button"
            className="shrink-0 gap-2 rounded-xl bg-indigo-700 text-white hover:bg-indigo-800"
          >
            <CopySimpleIcon aria-hidden size={16} />
            {t`Reprendre les données d’un CV`}
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t`Reprendre les données d’un autre CV`}</DialogTitle>
          <DialogDescription>
            {t`Toutes les informations, la photo et les rubriques du CV choisi remplaceront celles de ce CV. Le modèle, les couleurs et la mise en page seront conservés. Le CV d’origine restera intact.`}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <p role="status">{t`Chargement de vos CV…`}</p>
        ) : error ? (
          <p role="alert">{t`Impossible de charger vos CV. Fermez cette fenêtre et réessayez.`}</p>
        ) : choices.length === 0 ? (
          <p>{t`Vous n’avez pas encore d’autre CV à reprendre. Importez ou créez d’abord un autre CV.`}</p>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="resume-source">{t`CV à reprendre`}</Label>
            <select
              id="resume-source"
              value={selectedId}
              disabled={saving}
              onChange={(event) => setSelectedId(event.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white p-3 text-sm"
            >
              <option value="">{t`Choisir un CV`}</option>
              {choices.map((resume) => (
                <option key={resume.id} value={resume.id}>
                  {resume.title}
                </option>
              ))}
            </select>
          </div>
        )}
        {failure && (
          <p role="alert" className="text-sm text-rose-700">
            {failure}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={() => setOpen(false)}
          >{t`Annuler`}</Button>
          <Button
            type="button"
            disabled={saving || !selectedId || loading || !!error}
            onClick={() => void handleImport()}
          >
            {saving ? t`Importation…` : t`Remplacer toutes les données`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
